/**
 * Shared LLM model pricing + context-window catalog (as of 2026).
 *
 * SINGLE SOURCE OF TRUTH. Both free tools - the LLM Cost Calculator
 * ({@link ../components/tools/llm-cost-calculator.tsx}) and the Token & Context
 * Window Estimator ({@link ../components/tools/token-context-estimator.tsx}) -
 * import this module, so updating a price or a context window is a one-line edit
 * here that flows to every widget, test and calculation.
 *
 * The Claude numbers MUST stay consistent with the published
 * opus-vs-sonnet-vs-haiku comparison ({@link ../content/comparisons.ts}):
 *   - Opus  ~ USD 5 input  / USD 25 output per million tokens, 1M context.
 *   - Sonnet ~ USD 3 input  / USD 15 output per million tokens, 1M context.
 *   - Haiku  ~ USD 1 input  / USD 5  output per million tokens, large context.
 * A vitest guard ({@link ./model-pricing.test.ts}) pins exactly these figures so
 * the comparison and the tools can never silently drift apart.
 *
 * GPT and Gemini tiers are included as honest, dated reference points so users
 * can compare across providers. All figures are LIST rates "as of 2026" and move
 * over time; never present them as a live quote. No em dashes anywhere.
 */

/** Which company ships the model. Used for grouping and the provider label. */
export type ModelProvider = 'anthropic' | 'openai' | 'google'

/**
 * One model tier in the shared catalog. Prices are US dollars per ONE MILLION
 * tokens (the unit every major provider quotes), kept as plain numbers so the
 * widgets do pure, deterministic arithmetic with no rounding surprises.
 */
export type ModelPricing = {
  /** Stable, locale-independent id used as a React key and in tests. */
  id: string
  /** Human label shown in the UI, e.g. "Claude Opus 4.8". */
  name: string
  /** Short provider/family label, e.g. "Anthropic". */
  providerLabel: string
  provider: ModelProvider
  /** USD per 1,000,000 input (prompt) tokens. */
  inputPerMillion: number
  /** USD per 1,000,000 output (completion) tokens. */
  outputPerMillion: number
  /** Context window in tokens (max input + output the model can hold at once). */
  contextWindow: number
}

/**
 * The model catalog, "as of 2026". Ordered the way the UI should list it:
 * Claude tiers first (this is a Claude-focused academy), then GPT, then Gemini.
 *
 * Cross-provider figures are honest, dated reference points (mid-2026 list
 * rates) and are intentionally conservative round numbers; they are not a live
 * quote. The Claude figures are pinned to the opus-vs-sonnet-vs-haiku comparison.
 */
export const MODEL_PRICING: ModelPricing[] = [
  {
    id: 'claude-opus',
    name: 'Claude Opus 4.8',
    providerLabel: 'Anthropic',
    provider: 'anthropic',
    inputPerMillion: 5,
    outputPerMillion: 25,
    contextWindow: 1_000_000,
  },
  {
    id: 'claude-sonnet',
    name: 'Claude Sonnet 4.6',
    providerLabel: 'Anthropic',
    provider: 'anthropic',
    inputPerMillion: 3,
    outputPerMillion: 15,
    contextWindow: 1_000_000,
  },
  {
    id: 'claude-haiku',
    name: 'Claude Haiku 4.5',
    providerLabel: 'Anthropic',
    provider: 'anthropic',
    inputPerMillion: 1,
    outputPerMillion: 5,
    contextWindow: 200_000,
  },
  {
    id: 'gpt-5.5',
    name: 'GPT-5.5',
    providerLabel: 'OpenAI',
    provider: 'openai',
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    contextWindow: 400_000,
  },
  {
    id: 'gpt-5.5-mini',
    name: 'GPT-5.5 mini',
    providerLabel: 'OpenAI',
    provider: 'openai',
    inputPerMillion: 0.25,
    outputPerMillion: 2,
    contextWindow: 400_000,
  },
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    providerLabel: 'Google',
    provider: 'google',
    inputPerMillion: 1.25,
    outputPerMillion: 10,
    contextWindow: 1_000_000,
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    providerLabel: 'Google',
    provider: 'google',
    inputPerMillion: 0.3,
    outputPerMillion: 2.5,
    contextWindow: 1_000_000,
  },
]

/** The id of the model used as the default selection in both widgets. */
export const DEFAULT_MODEL_ID = 'claude-sonnet'

/** Look up a model by id; returns undefined when the id is unknown. */
export function getModel(id: string): ModelPricing | undefined {
  return MODEL_PRICING.find((model) => model.id === id)
}

/**
 * Heuristic English-text token estimate. We deliberately do NOT bundle a real
 * BPE tokenizer (it would be large, model-specific and still approximate), so we
 * use the widely cited ~4 characters per token rule and round up. This is an
 * ESTIMATE, never an exact count; the widgets state that plainly.
 */
export const CHARS_PER_TOKEN = 4

/**
 * Estimate the number of tokens in a piece of text. Pure and deterministic:
 * `chars / CHARS_PER_TOKEN`, rounded up, with empty/whitespace-only text at 0.
 */
export function estimateTokens(text: string): number {
  const length = text.trim().length
  if (length === 0) return 0
  return Math.ceil(length / CHARS_PER_TOKEN)
}

/** Discount applied to BOTH input and output when batch processing is on. */
export const BATCH_DISCOUNT = 0.5

/**
 * Fraction of INPUT tokens assumed to be served from a warm prompt cache when
 * the caching toggle is on, and the cost multiplier for those cached tokens.
 *
 * Cached input reads are about 10% of the normal input price on the major
 * providers in 2026. We assume a representative 75% of input is reused context
 * (system prompt, tools, RAG documents) once a cache is warm; this is an
 * illustrative default the user can reason about, not a guarantee.
 */
export const CACHE_HIT_FRACTION = 0.75
export const CACHED_INPUT_MULTIPLIER = 0.1

export type CostInput = {
  /** Input (prompt) tokens per single call. */
  inputTokens: number
  /** Output (completion) tokens per single call. */
  outputTokens: number
  /** Number of calls per month. */
  monthlyCalls: number
  /** Apply prompt caching to the input side. */
  promptCaching: boolean
  /** Apply the batch-processing discount to both sides. */
  batch: boolean
}

export type CostResult = {
  /** Cost of the input side of a single call, in USD. */
  inputCostPerCall: number
  /** Cost of the output side of a single call, in USD. */
  outputCostPerCall: number
  /** Total cost of a single call, in USD. */
  costPerCall: number
  /** Total cost across `monthlyCalls`, in USD. */
  monthlyCost: number
}

/**
 * Compute the per-call and monthly cost for a model under the given usage.
 *
 * Pure, deterministic and side-effect free so it is trivially unit-testable and
 * SSR-safe. All money is in USD; the widget formats it with Intl in the active
 * locale.
 *
 * Formula (per call):
 *   inputCost  = (inputTokens / 1e6) * inputPerMillion * inputModifier
 *   outputCost = (outputTokens / 1e6) * outputPerMillion * batchModifier
 *   where inputModifier blends cached vs uncached input when caching is on,
 *   and batchModifier halves both sides when batch is on.
 */
export function computeCost(
  model: ModelPricing,
  input: CostInput,
): CostResult {
  const batchModifier = input.batch ? BATCH_DISCOUNT : 1

  // Caching only reduces the INPUT side: a fraction of input is read from cache
  // at the discounted rate, the rest at the normal rate.
  const inputModifier = input.promptCaching
    ? (1 - CACHE_HIT_FRACTION) + CACHE_HIT_FRACTION * CACHED_INPUT_MULTIPLIER
    : 1

  const inputCostPerCall =
    (input.inputTokens / 1_000_000) *
    model.inputPerMillion *
    inputModifier *
    batchModifier

  const outputCostPerCall =
    (input.outputTokens / 1_000_000) *
    model.outputPerMillion *
    batchModifier

  const costPerCall = inputCostPerCall + outputCostPerCall
  const monthlyCost = costPerCall * Math.max(0, input.monthlyCalls)

  return {
    inputCostPerCall,
    outputCostPerCall,
    costPerCall,
    monthlyCost,
  }
}

/**
 * How full a model's context window is for a given token count. Returns the
 * fill fraction clamped to [0, 1] for the bar, the raw fraction (can exceed 1)
 * and whether the text fits. Pure and deterministic.
 */
export type ContextFit = {
  /** tokens / contextWindow, clamped to the 0..1 range for the progress bar. */
  fillFraction: number
  /** tokens / contextWindow, NOT clamped (can be > 1 when it overflows). */
  rawFraction: number
  /** Whether the token count is within the model's context window. */
  fits: boolean
  /** Tokens left before overflow (0 when it does not fit). */
  tokensRemaining: number
}

export function contextFit(
  tokens: number,
  model: ModelPricing,
): ContextFit {
  const rawFraction = tokens / model.contextWindow
  const fillFraction = Math.min(1, Math.max(0, rawFraction))
  const fits = tokens <= model.contextWindow
  const tokensRemaining = Math.max(0, model.contextWindow - tokens)
  return { fillFraction, rawFraction, fits, tokensRemaining }
}
